import { FC, useCallback } from 'react';
import { useFragment } from 'react-relay';
import { useField } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import InputAdornment from '@mui/material/InputAdornment';
import TextField from 'components/TextField';
import LanguageIndicator from 'components/LanguageIndicator';
import TranslateButton from 'components/TranslatableTextFieldTranslateButton';

import { TranslatableTextFieldInput_language$key } from '__generated__/TranslatableTextFieldInput_language.graphql';
import { TranslatableTextFieldInput_data$key } from '__generated__/TranslatableTextFieldInput_data.graphql';
import { TranslatableTextFieldInput_text$key } from '__generated__/TranslatableTextFieldInput_text.graphql';

type Props = {
  name: string;
  language: TranslatableTextFieldInput_language$key;
  text: TranslatableTextFieldInput_text$key | null;
  data: TranslatableTextFieldInput_data$key;
};

const TranslatableTextFieldInput: FC<Props> = ({ name, ...props }) => {
  const language = useFragment(
    graphql`
      fragment TranslatableTextFieldInput_language on Language {
        locale
        readOnly

        ...LanguageIndicator_language
        ...TranslatableTextFieldTranslateButton_to
      }
    `,
    props.language
  );

  const text = useFragment(
    graphql`
      fragment TranslatableTextFieldInput_text on Text {
        translations {
          edges {
            node {
              locale

              ...TranslatableTextFieldTranslateButton_textTranslation
            }
          }
        }
      }
    `,
    props.text
  );

  const data = useFragment(
    graphql`
      fragment TranslatableTextFieldInput_data on Query {
        languages {
          locale

          ...TranslatableTextFieldTranslateButton_from
        }

        ...TranslatableTextFieldTranslateButton_data
      }
    `,
    props.data
  );

  const [, , { setValue }] = useField<string>(`${name}.${language.locale}`);

  const { locale } = language;
  const { languages } = data;

  const handleTranslate = useCallback((value: string) => {
    setValue(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TextField
      fullWidth
      name={`${name}.${locale}`}
      disabled={language.readOnly}
      sx={{ mb: 1 }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <LanguageIndicator
              language={language}
              sx={{ width: 16, height: 16 }}
            />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            {languages
              .filter(from => from.locale !== locale)
              .map(from => (
                <TranslateButton
                  key={from.locale}
                  name={name}
                  from={from}
                  to={language}
                  textTranslation={
                    text?.translations?.edges.find(
                      ({ node }) => node.locale === from.locale
                    )?.node ?? null
                  }
                  data={data}
                  onTranslate={handleTranslate}
                />
              ))}
          </InputAdornment>
        ),
      }}
    />
  );
};

export default TranslatableTextFieldInput;
